import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import StatCard from './StatCard';

const OverviewTab = ({ token, dateRange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOverview = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/api/analytics/dashboard/overview?startDate=${dateRange.start}&endDate=${dateRange.end}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        const result = await response.json();

        if (result.success) {
          setData(result.data);
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Erreur de chargement des donnees');
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [token, dateRange]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '32px', marginBottom: '16px' }}>...</div>
        <p>Chargement des statistiques...</p>
      </div>
    );
  }

  if (error || !data?.available) {
    return (
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '32px', marginBottom: '16px' }}>!</div>
        <h3 style={{ margin: '0 0 8px 0' }}>Donnees non disponibles</h3>
        <p style={{ margin: 0, color: '#856404' }}>
          {data?.message || error || 'La Phase 1 (Analytics & Audit) doit etre deployee.'}
        </p>
      </div>
    );
  }

  const { general, moduleStats, dailyStats, topUsers } = data;

  return (
    <div>
      {/* Cartes de statistiques */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <StatCard
          icon="#"
          title="Evenements"
          value={general?.total_events || 0}
          color="#9c27b0"
        />
        <StatCard
          icon="@"
          title="Utilisateurs uniques"
          value={general?.unique_users || 0}
          color="#2196f3"
        />
        <StatCard
          icon="*"
          title="Sessions"
          value={general?.sessions || 0}
          color="#4caf50"
        />
        <StatCard
          icon="!"
          title="Actions d'audit"
          value={general?.audit_events || 0}
          color="#ff9800"
        />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '24px'
      }}>
        {/* Modules les plus visites */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>
            Modules les plus visites
          </h3>
          {moduleStats && moduleStats.length > 0 ? (
            <div>
              {moduleStats.map((mod, idx) => (
                <div
                  key={mod.module}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 0',
                    borderBottom: idx < moduleStats.length - 1 ? '1px solid #eee' : 'none'
                  }}
                >
                  <span style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: `hsl(${280 - idx * 30}, 70%, 50%)`,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {idx + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                      {mod.module}
                    </div>
                    <div style={{
                      height: '6px',
                      background: '#eee',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${(mod.visits / moduleStats[0].visits) * 100}%`,
                        background: `hsl(${280 - idx * 30}, 70%, 50%)`,
                        borderRadius: '3px'
                      }} />
                    </div>
                  </div>
                  <span style={{ color: '#666', fontSize: '14px' }}>
                    {mod.visits} visites
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#666', textAlign: 'center' }}>Aucune donnee</p>
          )}
        </div>

        {/* Top utilisateurs */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>
            Utilisateurs les plus actifs
          </h3>
          {topUsers && topUsers.length > 0 ? (
            <div>
              {topUsers.slice(0, 5).map((user, idx) => (
                <div
                  key={user.user_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 0',
                    borderBottom: idx < 4 ? '1px solid #eee' : 'none'
                  }}
                >
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, hsl(${200 + idx * 20}, 70%, 50%), hsl(${220 + idx * 20}, 70%, 40%))`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600'
                  }}>
                    {user.user_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500' }}>{user.user_name || 'Inconnu'}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                  </div>
                  <span style={{
                    background: '#e3f2fd',
                    color: '#1976d2',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {user.actions} actions
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#666', textAlign: 'center' }}>Aucune donnee</p>
          )}
        </div>
      </div>

      {/* Graphique journalier (simplifie) */}
      {dailyStats && dailyStats.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          marginTop: '24px'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>
            Activite quotidienne
          </h3>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            height: '150px',
            padding: '20px 0'
          }}>
            {dailyStats.map((day, idx) => {
              const maxEvents = Math.max(...dailyStats.map(d => d.events));
              const height = maxEvents > 0 ? (day.events / maxEvents) * 100 : 0;

              return (
                <div
                  key={day.date}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  title={`${day.date}: ${day.events} evenements, ${day.users} utilisateurs`}
                >
                  <div style={{
                    width: '100%',
                    maxWidth: '40px',
                    height: `${height}%`,
                    minHeight: '4px',
                    background: 'linear-gradient(180deg, #9c27b0, #7b1fa2)',
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease'
                  }} />
                  <span style={{
                    fontSize: '10px',
                    color: '#666',
                    transform: 'rotate(-45deg)',
                    whiteSpace: 'nowrap'
                  }}>
                    {new Date(day.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
